import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Button, Grid, Divider, FormControlLabel, Checkbox, RadioGroup, Radio } from '@mui/material';
import { PaymentDto, SavedPaymentPayload } from '@/app/models/checkout/types';
import RewardPointsSection, { RewardPointsHandle } from './rewardPointsSection';

export interface PaymentInformationProps {
  open: boolean;
  payment: PaymentDto;
  showOnAccountRequest: boolean | null;
  useImpactRewards: boolean;
  enablePromotionsSection: boolean;
  onSave: (payload: SavedPaymentPayload) => void;
  initialPayment?: SavedPaymentPayload;
}

const computeInitialTerms = (
  isOnAccountEnabled: boolean,
  cached?: 'Cash' | 'On Account' | string | null
): 'Cash' | 'On Account' => {
  if (cached === 'Cash' || cached === 'On Account') {
    if (cached === 'On Account' && !isOnAccountEnabled) return 'Cash';
    return cached;
  }
  return isOnAccountEnabled ? 'On Account' : 'Cash';
};

const PaymentInformation: React.FC<PaymentInformationProps> = ({
  open,
  payment,
  showOnAccountRequest,
  useImpactRewards,
  enablePromotionsSection,
  onSave,
  initialPayment
}) => {
  const isOnAccountEnabled =
    payment.sellType === 'y' || payment.sellType === 'o' || payment.onHoldDueInactivity;

  const [selectedPayment, setSelectedPayment] = useState<'Cash' | 'On Account'>(() =>
    computeInitialTerms(isOnAccountEnabled, initialPayment?.paymentTerms as any)
  );

  useEffect(() => {
    if (!isOnAccountEnabled && selectedPayment === 'On Account') {
      setSelectedPayment('Cash');
    }
  }, [isOnAccountEnabled]);

  const [interestedOnAccount, setInterestedOnAccount] = useState<boolean>(
    !!initialPayment?.interestedOnAccount
  );

  const rewardRef = useRef<RewardPointsHandle>(null);

  useEffect(() => {
    if (!open || !enablePromotionsSection) return;
    const cached = initialPayment?.selectedRewardPoints;
    if (cached !== undefined && cached !== null && cached !== '') {
      rewardRef.current?.setValue(cached);
    }
  }, [open, enablePromotionsSection, initialPayment]);

  const handleContinue = () => {
    let selectedRewardPoints: number | string = '';
    if (enablePromotionsSection) {
      const isValid = rewardRef.current?.validate() ?? true;
      if (!isValid) return;
      selectedRewardPoints = rewardRef.current?.getValue() ?? '';
    }
    onSave({
      interestedOnAccount,
      paymentTerms: selectedPayment,
      selectedRewardPoints
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box className="circleNumCheckout" sx={{ mr: 2 }}>
          <Typography className="circleNumCheckoutTypo">3</Typography>
        </Box>
        <Typography className="checkout-container-title" gutterBottom>
          Payment
        </Typography>
        <Divider className="checkout-divider" />
      </Box>

      {open && (
        <Box sx={{ border: '10px solid', borderColor: 'grey.300', borderRadius: 2, p: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={12}>
              <RadioGroup
                row
                value={selectedPayment}
                onChange={(e) => setSelectedPayment(e.target.value as 'Cash' | 'On Account')}
              >
                <FormControlLabel value="Cash" control={<Radio color="primary" />} label="Cash" />
                <FormControlLabel
                  value="On Account"
                  control={<Radio color="primary" />}
                  label="On Account"
                  disabled={!isOnAccountEnabled}
                  style={{ color: !isOnAccountEnabled ? '#EBEBE4' : 'inherit' }}
                />
              </RadioGroup>

              {selectedPayment === 'Cash' && (
                <Typography color="error" sx={{ mt: 1, mb: 2, fontSize: '10px' }}>
                  Branch may contact you for deposit on this cash order
                </Typography>
              )}

              {selectedPayment === 'Cash' &&
                showOnAccountRequest &&
                payment.sellType !== 'y' &&
                payment.sellType !== 'o' &&
                !payment.onHoldDueInactivity && (
                <Box sx={{ mt: 2, ml: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="InterestedOnAccount"
                        checked={interestedOnAccount}
                        onChange={(e) => setInterestedOnAccount(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '12px', whiteSpace: 'normal' }}>
                        Please contact me about opening a Lansing Business Account for future credit purchases.
                      </Typography>
                    }
                  />
                </Box>
              )}

              {enablePromotionsSection && (
                <RewardPointsSection
                  ref={rewardRef}
                  useRewardPoints={useImpactRewards}
                  payment={payment}
                  selectedPaymentMethod="CreditCard"
                />
              )}
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="primary"
              className="blueButtonOutline"
              onClick={handleContinue}
            >
              Continue
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PaymentInformation;
